import { CheckIcon, CloseIcon, WarningTriangleIcon } from "@xola/icons";
import { Dot, formatTime, Phone, Table } from "@xola/ui-kit";
import { ColDef } from "ag-grid-community";
import clsx from "clsx";
import { DateFormat, DemographicIcon, formatDate, isEquipmentItem } from "common";
import { debounce, startCase } from "lodash";
import React, { useLayoutEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
    EquipmentUnit,
    Event,
    Experience,
    ExperiencePurchaseItem,
    FieldResponse,
    FieldType,
    Guest,
    GuestStatus,
    Seller,
    SourceCopy,
    Timeslot,
} from "types";
import { EventBanner, EventBannerMode } from "../../Common/EventBanner";
import { RosterColumnId } from "./RosterGuestList/helpers";
import { getQuestionnaireValue } from "./RosterGuestList/QuestionnaireCell";
import { RosterGuestListForPrintRow, RosterRow } from "./RosterGuestTypes";
import { Currency } from "@/components/Common/Currency";
import { useSeller } from "@/hooks/useAccount";
import { useResource } from "@/hooks/useResource";
import { isVirtualExperience } from "@/models/Experience";
import {
    getAddonLineItems,
    getAttendeeLineItemsForRoster,
    getDemographicLineItems,
    getEquipmentRosterLineItems,
} from "@/models/PurchaseItem";
import { getVoucherCode } from "@/models/Ticket";
import { DashboardRosterColumnsConfig } from "@/types/Dashboard";

interface Props {
    rowData: RosterRow[];
    timeslot: Timeslot;
    event?: Event;
    columnDefs: ColDef[];
}

interface RosterTableCellProps {
    columnConfig?: ColumnConfig;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    colSpan?: number;
}

const RosterTableCell = ({ columnConfig, children, className, style, colSpan }: RosterTableCellProps) => (
    <Table.Cell
        className={clsx(columnConfig?.className, "!text-xs", className)}
        style={{ width: columnConfig?.width, ...style }}
        colSpan={colSpan}
    >
        {children}
    </Table.Cell>
);

export const columnsToExclude = new Set([RosterColumnId.ACTION]);

const debouncedRosterPrint = debounce(() => {
    // 1 sec timeout to allow the component to be completely rendered before opening the print dialog
    setTimeout(() => window.print(), 1000);
}, 200);

export const RosterGuestListForPrint = ({ rowData, timeslot, columnDefs, event }: Props) => {
    const print = useLocation<{ print?: boolean }>().state?.print ?? false;
    const { data: experience } = useResource<Experience>(["products", timeslot.product]);
    const isVirtual = (!!event?.virtualMeeting || isVirtualExperience(experience)) ?? false;
    const visibleColumns = columnDefs.filter(
        (column) => !column.hide && !columnsToExclude.has(column.colId as RosterColumnId),
    );

    const visibleColumnIds = visibleColumns.map((column) => column.colId as RosterColumnId);
    const dynamicColumnWidths = getDynamicColumnWidths(visibleColumnIds);

    // Allows us to invoke printing from the dashboard list event menu
    // Using layout effect to allow the component to be completely rendered before opening the print dialog
    useLayoutEffect(() => {
        if (print && visibleColumns.length > 0) {
            debouncedRosterPrint();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [print]);

    if (!experience) {
        return null;
    }

    return (
        <div className="no-translate absolute inset-0 hidden print:visible print:block">
            {experience ? (
                <div className="mb-4">
                    <EventBanner
                        product={experience ?? { id: timeslot.product }}
                        timeslot={timeslot}
                        isVirtual={isVirtual}
                        mode={EventBannerMode.PRINT}
                    />
                </div>
            ) : null}

            {event?.notes && <EventNotes event={event} />}

            {rowData.map((row, index) => {
                if (!("item" in row)) {
                    return null;
                }

                const questionnaires = getQuestionnaireFields(row);
                const isOrganizerRow = row.path.length === 1;

                /**
                 * we are rendering one table for every one row, becuase, if we have a single table and if it have
                 * lot of bookings, the table is trying to move to next page as it tries to not to break pages. one
                 * advantage with this is we show table header for every booking. otherwise the other page bookings
                 * won't be able to identify which column, the data is mapped to as we render only once in the first
                 * page.
                 */
                return (
                    /** Using combination as key since we will get related to key
+                     * error when we make 2 bookings for same organizer */
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`table-${row.item?.id}-${index}`} className="print-table-container">
                        <Table
                            className={clsx(isOrganizerRow && "!mt-2")}
                            style={{ tableLayout: "fixed", width: "100%" }}
                        >
                            {isOrganizerRow ? (
                                <HeaderRow
                                    columnDefs={
                                        visibleColumns && visibleColumns.length > 0 ? visibleColumns : columnDefs
                                    }
                                    dynamicColumnWidths={dynamicColumnWidths}
                                />
                            ) : null}

                            <Table.Body>
                                <>
                                    <Row
                                        item={row.item as ExperiencePurchaseItem}
                                        guest={row.guest}
                                        equipment={row.equipment}
                                        rosterSection={row.rosterSection}
                                        organizer={row.organizer}
                                        isOrganizer={row.isOrganizer}
                                        lineItem={row.demographic}
                                        purchaseNotes={row.purchaseNotes}
                                        guestNotes={row.guestNotes}
                                        columnDefsOrder={visibleColumns}
                                        dynamicColumnWidths={dynamicColumnWidths}
                                    />
                                    {questionnaires.length > 0 && (
                                        <QuestionnaireRow data={questionnaires} columns={columnDefs.length} />
                                    )}
                                </>
                            </Table.Body>
                        </Table>
                    </div>
                );
            })}
        </div>
    );
};

export const HeaderRow = ({
    columnDefs,
    dynamicColumnWidths,
}: {
    columnDefs: ColDef[];
    dynamicColumnWidths: Record<string, { className: string; width: string }>;
}) => {
    return (
        <Table.Head>
            <Table.Row>
                {columnDefs.map((column) => (
                    <Table.Header
                        key={column.colId ?? column.headerName}
                        className={`!text-xs ${dynamicColumnWidths[column?.colId as RosterColumnId]?.className || ""}`}
                        style={{
                            width: dynamicColumnWidths[column?.colId as RosterColumnId]?.width,
                        }}
                    >
                        {column.headerName}
                    </Table.Header>
                ))}
            </Table.Row>
        </Table.Head>
    );
};

export const Row = ({
    item,
    lineItem,
    guest,
    equipment,
    rosterSection,
    organizer,
    purchaseNotes = [],
    guestNotes,
    isOrganizer,
    columnDefsOrder,
    dynamicColumnWidths,
}: RosterGuestListForPrintRow) => {
    const seller = useSeller();
    const sectionName = rosterSection ? startCase(rosterSection) : undefined;
    const name = guest?.name ?? equipment?.name ?? sectionName ?? organizer?.name;
    const hideOrganizerName = name === "Organizer";
    const { createdAt } = item.purchase;
    const purchaseDate = formatDate(createdAt, DateFormat.DATE_SHORT, true);

    const { tags } = item.purchase;
    const ids = tags ? tags.map((tag) => tag.id) : [];
    const formattedTagsString = ids.join(", ");

    const demographicsFormattedList = useMemo(() => {
        const list: { [key: string]: number } = {};
        // For equipment items the "demographics" are the attendees (demographic_secondary); equipment
        // itself is shown in the Equipment column below.
        const lineItems = isEquipmentItem(item) ? getAttendeeLineItemsForRoster(item) : getDemographicLineItems(item);

        for (const demographic of lineItems) {
            if ((demographic.quantity ?? 0) > 0) {
                list[demographic.name] = demographic.quantity ?? 0;
            }
        }

        return list;
    }, [item]);

    const equipmentFormattedList = useMemo(() => {
        const list: { [key: string]: number } = {};
        for (const equipment of getEquipmentRosterLineItems(item)) {
            if ((equipment.quantity ?? 0) > 0) {
                list[equipment.name] = equipment.quantity ?? 0;
            }
        }

        return list;
    }, [item]);

    const addOns: { [key: string]: number } = {};
    for (const addon of getAddonLineItems(item)) {
        if (addon.name) {
            if (!addOns[addon.name]) {
                addOns[addon.name] = 0;
            }

            addOns[addon.name] += addon.quantity ?? 0;
        }
    }

    return (
        <Table.Row className={clsx("no-translate border-t border-t-gray-lighter !text-xs", isOrganizer && "font-bold")}>
            {columnDefsOrder.map((column) => {
                if (
                    column.hide ||
                    columnsToExclude.has(
                        ((column as DashboardRosterColumnsConfig)?.id || (column as ColDef)?.colId) as RosterColumnId,
                    )
                ) {
                    return null;
                }

                const columnId = ((column as DashboardRosterColumnsConfig)?.id ||
                    (column as ColDef)?.colId) as RosterColumnId;
                const columnConfig = dynamicColumnWidths?.[columnId] ?? columnWidths[columnId];
                const headerName = (column as ColDef)?.headerName;

                return renderCellForColumn(columnId, columnConfig, {
                    isOrganizer,
                    item,
                    name,
                    hideOrganizerName,
                    demographicsFormattedList,
                    equipmentFormattedList,
                    lineItem,
                    equipment,
                    rosterSection,
                    purchaseNotes,
                    guestNotes,
                    guest,
                    purchaseDate,
                    seller,
                    addOns,
                    formattedTagsString,
                    columnHeaderName: headerName,
                });
            })}
        </Table.Row>
    );
};

interface RenderProps {
    isOrganizer?: boolean;
    item: ExperiencePurchaseItem;
    name?: string;
    hideOrganizerName: boolean;
    demographicsFormattedList: Record<string, number>;
    equipmentFormattedList: Record<string, number>;
    lineItem?: { name?: string };
    equipment?: EquipmentUnit;
    rosterSection?: "attendees" | "equipment";
    purchaseNotes?: Array<{ id: string; text: string }>;
    guestNotes?: Array<{ id: string; text: string }>;
    guest?: Guest;
    purchaseDate: string;
    seller: Seller;
    addOns: Record<string, number>;
    formattedTagsString: string;
    columnHeaderName?: string;
}

interface ColumnConfig {
    className?: string;
    width?: string;
}

const renderProductTimeGuestDemographics = (
    columnId: RosterColumnId,
    columnConfig: ColumnConfig,
    props: RenderProps,
) => {
    const {
        isOrganizer,
        item,
        name,
        hideOrganizerName,
        demographicsFormattedList,
        equipmentFormattedList,
        lineItem,
        equipment,
        rosterSection,
    } = props;

    // A per-unit equipment row (equipment) or the equipment section-summary row belongs in the
    // Equipment column; attendee/demographic rows belong in the Demographics column.
    const isEquipmentRow = Boolean(equipment) || rosterSection === "equipment";
    // Section-summary rows repeat the item breakdown in their own column (Attendees -> Demographics,
    // Equipment -> Equipment).
    const showDemographicsBreakdown = Boolean(isOrganizer) || rosterSection === "attendees";
    const showEquipmentBreakdown = Boolean(isOrganizer) || rosterSection === "equipment";

    switch (columnId) {
        case RosterColumnId.PRODUCT:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    <span className="keep-all whitespace-normal">{isOrganizer ? item.name : ""}</span>
                </RosterTableCell>
            );

        case RosterColumnId.TIME:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {isOrganizer && item.arrivalTime !== undefined ? formatTime(item.arrivalTime, DateFormat.TIME) : ""}
                </RosterTableCell>
            );

        case RosterColumnId.GUEST:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    <span className="keep-all whitespace-normal">
                        {isOrganizer ? `${hideOrganizerName ? "" : name} (Organizer)` : name}
                    </span>
                </RosterTableCell>
            );

        case RosterColumnId.DEMOGRAPHICS:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {showDemographicsBreakdown ? (
                        <ul className="mb-2 mt-2 leading-5">
                            {Object.keys(demographicsFormattedList).map((demographicName: string) => (
                                <li
                                    key={demographicName}
                                    className="flex items-center space-x-1"
                                    data-testid="roster-guest-print-list-demographics-item"
                                >
                                    <span>
                                        {demographicsFormattedList[demographicName]} {demographicName}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : !isEquipmentRow && lineItem?.name ? (
                        <span className="mt-1 mb-1 flex h-full items-center space-x-1">
                            <DemographicIcon name={lineItem.name} />
                            <span>{lineItem.name}</span>
                        </span>
                    ) : null}
                </RosterTableCell>
            );

        case RosterColumnId.EQUIPMENT:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {showEquipmentBreakdown ? (
                        <ul className="mb-2 mt-2 leading-5">
                            {Object.keys(equipmentFormattedList).map((equipmentName: string) => (
                                <li
                                    key={equipmentName}
                                    className="flex items-center space-x-1"
                                    data-testid="roster-guest-print-list-equipment-item"
                                >
                                    <span>
                                        {equipmentFormattedList[equipmentName]} {equipmentName}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : equipment?.name ? (
                        <span className="mt-1 mb-1 flex h-full items-center">{equipment.name}</span>
                    ) : null}
                </RosterTableCell>
            );

        default:
            return null;
    }
};

const renderAmountsAndReservationCodes = (columnId: RosterColumnId, columnConfig: ColumnConfig, props: RenderProps) => {
    const { isOrganizer, item, seller } = props;

    switch (columnId) {
        case RosterColumnId.BALANCE_DUE:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {isOrganizer && item.purchase.balance > 0 && (
                        <Currency currency={seller.currency}>{item.purchase.balance}</Currency>
                    )}
                </RosterTableCell>
            );

        case RosterColumnId.GROSS_PURCHASE_AMOUNT:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    <Currency currency={seller.currency}>{item.purchase.amount ?? 0}</Currency>
                </RosterTableCell>
            );

        case RosterColumnId.GROSS_RESERVATION_AMOUNT:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    <Currency currency={seller.currency}>{item.amount ?? 0}</Currency>
                </RosterTableCell>
            );

        case RosterColumnId.RESERVATION_CODE:
            return <RosterTableCell columnConfig={columnConfig}>{item.shortCode.toUpperCase()}</RosterTableCell>;

        default:
            return null;
    }
};

const renderContactCheckIn = (columnId: RosterColumnId, columnConfig: ColumnConfig, props: RenderProps) => {
    const { isOrganizer, item, guest } = props;

    switch (columnId) {
        case RosterColumnId.PHONE:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    <div className="whitespace-normal break-words break-all">
                        {isOrganizer && <Phone>{item.purchase.phone}</Phone>}
                    </div>
                </RosterTableCell>
            );
        case RosterColumnId.EMAIL:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    <div className="whitespace-normal break-words break-all">
                        {isOrganizer ? item.purchase.customerEmail : guest?.traveler?.email ?? null}
                    </div>
                </RosterTableCell>
            );
        case RosterColumnId.SOURCE:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {isOrganizer ? (SourceCopy as any)[item.source] ?? startCase(item.source) : null}
                </RosterTableCell>
            );
        case RosterColumnId.CHECK_IN:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {guest &&
                        (guest.guestStatus === GuestStatus.ARRIVED ? (
                            <CheckIcon />
                        ) : guest.guestStatus === GuestStatus.NO_SHOW ? (
                            <CloseIcon />
                        ) : null)}
                </RosterTableCell>
            );
        case RosterColumnId.VOUCHERS:
            return (
                <RosterTableCell columnConfig={columnConfig} className="font-bold">
                    {isOrganizer && item.tickets?.length ? getVoucherCode(item.tickets, "--") : "--"}
                </RosterTableCell>
            );
        default:
            return null;
    }
};

const renderNotesWaiversAddon = (columnId: RosterColumnId, columnConfig: ColumnConfig, props: RenderProps) => {
    const { isOrganizer, purchaseNotes, guestNotes, guest, purchaseDate, addOns, formattedTagsString } = props;

    switch (columnId) {
        case RosterColumnId.NOTES:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {isOrganizer && purchaseNotes && purchaseNotes.length > 0 ? (
                        <ul className="list-disc py-2 leading-5">
                            {purchaseNotes.map((note) => (
                                <li key={note.id} className="break-word whitespace-normal">
                                    {note.text}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="list-disc py-2 leading-5">
                            {guestNotes?.map((note: any) => (
                                <li key={note.id} className="break-word whitespace-normal">
                                    {note.text}
                                </li>
                            ))}
                        </ul>
                    )}
                </RosterTableCell>
            );
        case RosterColumnId.WAIVERS:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {guest && (guest?.waiver ? <CheckIcon /> : <WarningTriangleIcon />)}
                </RosterTableCell>
            );
        case RosterColumnId.PURCHASE_DATE:
            return <RosterTableCell columnConfig={columnConfig}>{purchaseDate}</RosterTableCell>;
        case RosterColumnId.ADDONS:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {isOrganizer ? (
                        <ul>
                            {Object.entries(addOns)
                                .filter(([, quantity]) => (quantity as number) > 0)
                                .map(([name, quantity]) => (
                                    <li key={name}>
                                        <span className="whitespace-normal break-all">
                                            {name}: {quantity}
                                        </span>
                                    </li>
                                ))}
                        </ul>
                    ) : (
                        guest?.formResponse?.fields && (
                            <ul>
                                {guest.formResponse.fields
                                    .filter(
                                        (field: any) =>
                                            field.type === FieldType.ADDON && field.value && Number(field.value) > 0,
                                    )
                                    .map((field: any) => (
                                        <li key={`${field.label}-${field.value}`}>
                                            <span className="whitespace-normal break-all">
                                                {field.label}: {field.value}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                        )
                    )}
                </RosterTableCell>
            );
        case RosterColumnId.TAGS:
            return (
                <RosterTableCell columnConfig={columnConfig}>
                    {isOrganizer ? <span className="whitespace-normal break-all"> {formattedTagsString}</span> : null}
                </RosterTableCell>
            );
        default:
            return null;
    }
};

const renderCellForColumn = (columnId: RosterColumnId, columnConfig: ColumnConfig, props: RenderProps) => {
    const basicResult = renderProductTimeGuestDemographics(columnId, columnConfig, props);
    if (basicResult) return basicResult;

    const amountsResult = renderAmountsAndReservationCodes(columnId, columnConfig, props);
    if (amountsResult) return amountsResult;

    const miscResult = renderNotesWaiversAddon(columnId, columnConfig, props);
    if (miscResult) return miscResult;

    const contactResult = renderContactCheckIn(columnId, columnConfig, props);
    if (contactResult) return contactResult;

    const { isOrganizer, item, guest, columnHeaderName } = props;

    return (
        <RosterTableCell columnConfig={columnConfig} className="!whitespace-normal">
            {getQuestionnaireValue(isOrganizer ? item.formResponse : guest?.formResponse, columnHeaderName)}
        </RosterTableCell>
    );
};

interface QuestionnaireRowProps {
    data: FieldResponse[];

    /** Total number of columns in a Guest row. We need this for questionnaire cell colspan */
    columns: number;
}

export const QuestionnaireRow = ({ data, columns }: QuestionnaireRowProps) => {
    return (
        <Table.Row>
            <RosterTableCell colSpan={columns}>
                <div className="grid grid-cols-3 gap-x-1 gap-y-2">
                    {data.map((field) => (
                        <span key={field.label} className="whitespace-pre-line break-words">
                            <strong>{field.label}</strong>: {field.value} {field.unit ?? null}
                        </span>
                    ))}
                </div>
            </RosterTableCell>
        </Table.Row>
    );
};

interface EventNotesProps {
    event?: Event;
}
const EventNotes = ({ event }: EventNotesProps) => {
    return (
        <div className="mb-2 !text-sm">
            <div className="font-bold">Event Notes</div>

            {event?.notes.map((note) => {
                return (
                    <div key={note.id}>
                        <Dot className="bg-black align-middle " /> {note.text}
                    </div>
                );
            })}
        </div>
    );
};

export const getQuestionnaireFields = (row: RosterRow): FieldResponse[] => {
    if (!("guest" in row)) return [];

    return (row.guest && !row.isOrganizer && row.guest.formResponse?.fields.filter((field) => field.roster)) || [];
};

const baseColumnConfig: {
    [key in RosterColumnId]: { className: string; weight: number };
} = {
    [RosterColumnId.GUEST]: { className: "overflow-hidden text-ellipsis", weight: 18 },
    [RosterColumnId.DEMOGRAPHICS]: { className: "text-left", weight: 8 },
    [RosterColumnId.EQUIPMENT]: { className: "text-left", weight: 8 },
    [RosterColumnId.BALANCE_DUE]: { className: "text-right", weight: 10 },
    [RosterColumnId.NOTES]: { className: "overflow-hidden text-ellipsis whitespace-nowrap text-left", weight: 12 },
    [RosterColumnId.WAIVERS]: { className: "text-center", weight: 7 },
    [RosterColumnId.PURCHASE_DATE]: { className: "text-center", weight: 7 },
    [RosterColumnId.GROSS_PURCHASE_AMOUNT]: { className: "text-right", weight: 7 },
    [RosterColumnId.GROSS_RESERVATION_AMOUNT]: { className: "text-right", weight: 7 },
    [RosterColumnId.RESERVATION_CODE]: { className: "text-center", weight: 7 },
    [RosterColumnId.ADDONS]: { className: "text-left", weight: 7 },
    [RosterColumnId.TAGS]: { className: "text-left", weight: 7 },
    [RosterColumnId.PHONE]: { className: "text-center", weight: 7 },
    [RosterColumnId.EMAIL]: { className: "text-left", weight: 10 },
    [RosterColumnId.SOURCE]: { className: "text-left", weight: 10 },
    [RosterColumnId.CHECK_IN]: { className: "text-center", weight: 7 },
    [RosterColumnId.VOUCHERS]: { className: "text-left", weight: 7 },
    [RosterColumnId.PRODUCT]: { className: "overflow-hidden text-ellipsis text-left", weight: 12 },
    [RosterColumnId.TIME]: { className: "text-left", weight: 11 },
    [RosterColumnId.ACTION]: { className: "", weight: 0 },
    [RosterColumnId.COLLAPSE]: { className: "", weight: 0 },
};

/**
 * Calculate dynamic column widths based on visible columns to ensure total width never exceeds 100%
 * Handles both predefined columns and dynamic/unknown columns with a default weight of 10
 */
export const getDynamicColumnWidths = (
    visibleColumnIds: RosterColumnId[],
): {
    [key in RosterColumnId]: { className: string; width: string };
} => {
    const DEFAULT_WEIGHT = 10;
    const DEFAULT_CLASS_NAME = "text-left";

    const activeColumns = visibleColumnIds.filter((id) => !columnsToExclude.has(id));

    let totalWeight = 0;
    for (const id of activeColumns) {
        const config = baseColumnConfig[id];
        if (config) {
            totalWeight += config.weight > 0 ? config.weight : 0;
        } else {
            totalWeight += DEFAULT_WEIGHT;
        }
    }

    const dynamicWidths = {} as { [key in RosterColumnId]: { className: string; width: string } };

    for (const key of Object.keys(baseColumnConfig)) {
        const columnId = key as RosterColumnId;
        const config = baseColumnConfig[columnId];

        if (config.weight === 0) {
            dynamicWidths[columnId] = { className: config.className, width: "0%" };
        } else if (activeColumns.includes(columnId)) {
            const percentage = ((config.weight / totalWeight) * 100).toFixed(2);
            dynamicWidths[columnId] = { className: config.className, width: `${percentage}%` };
        } else {
            dynamicWidths[columnId] = { className: config.className, width: "0%" };
        }
    }

    for (const columnId of activeColumns) {
        if (!baseColumnConfig[columnId]) {
            const percentage = ((DEFAULT_WEIGHT / totalWeight) * 100).toFixed(2);
            dynamicWidths[columnId] = { className: DEFAULT_CLASS_NAME, width: `${percentage}%` };
        }
    }

    return dynamicWidths;
};

export const columnWidths: {
    [key in RosterColumnId]: { className: string; width: string };
} = getDynamicColumnWidths(Object.keys(baseColumnConfig) as RosterColumnId[]);
